import type {
  BaseEventPayload,
  DragLocationHistory,
  ElementDragPayload,
  ElementDragType,
} from "@atlaskit/pragmatic-drag-and-drop/dist/types/internal-types";
import invariant from "tiny-invariant";
import { useCallback, useMemo, type RefObject } from "react";
import { z } from "zod";
import { getAfterShipmentId, parseDroppableId } from "../../../utils/dragging";
import { getShipmentIdsFromSelection } from "../../../utils/selectionHelper";
import type {
  AssignShipmentFn,
  UnassignShipmentFn,
} from "../../../types/assignShipment.type";
import { useSelectionContext } from "../../../context/SelectionContext";
import { useTableSelectionParam } from "../../../hooks/useTableSelectionParam";
import type {
  PlanSummary,
  PlanTableFormat,
} from "../../../types/planning/plan.type";
import { toast } from "../../../utils/snackbarHelper";
import { toastMessage } from "../../../constants/strings";
import type { ReorderPayload } from "../../../hooks/react-query/useReorderShipments";
import type {
  AssignToRequest,
  Shipment,
  UnassignRequest,
} from "../../../services/prePlanningService.types";
import { getDropMetrics } from "../../../utils/dropHelpers";
import { useIsFeatureFlagEnabled } from "../../../featureFlags/useIsFeatureFlagEnabled";
import {
  isRouteClosedForSequencing,
  isRouteImmutablePure,
} from "../../../hooks/useIsRouteImmutable";
import { checkIsTrapCancelled } from "../../../hooks/useIsTrapCancelled";
import { getAfterRouteStopParam, getFormattedPlannedRoutes } from "./utils";
import type {
  ConfirmShipmentMoveDialogRef,
  ShipmentToMove,
} from "./ConfirmShipmentMoveDialog";
import type { ShipmentActionDialogRef } from "./ShipmentActionDialog";

type ActionType = "assign" | "unassign";

const actionMap: Record<string, ActionType> = {
  "sidebar->mainTable": "assign",
  "unassigned->mainTable": "assign",
  "unassigned->shipmentTable": "assign",
  "unassigned->sidebar": "assign",
  "shipmentTable->sidebar": "assign",
  "sidebar->shipmentTable": "assign",
  "sidebar->sidebar": "assign",
  "shipmentTable->shipmentTable": "assign",
  "sidebar->unassigned": "unassign",
  "shipmentTable->unassigned": "unassign",
};

type Drop = {
  source: ElementDragPayload;
  location: DragLocationHistory;
  handleAssignShipments: AssignShipmentFn;
  handleUnassignShipments: UnassignShipmentFn;
};

export const useInboundDragAndDrop = () => {
  const { selected: sidebarSelection, clearSelection } = useSelectionContext();
  const { selection: tableSelection } = useTableSelectionParam();

  const handleDrop = ({
    source,
    location,
    handleAssignShipments,
    handleUnassignShipments,
  }: Drop) => {
    const isValidDestination = location.current.dropTargets.length;
    if (!isValidDestination) {
      return;
    }

    const destination = parseDroppableId(
      String(location.current.dropTargets[0].data.id)
    );

    const destinationIndex = Number(location.current.dropTargets[0].data.index);

    const origin = parseDroppableId(String(source.data.id));

    if (destination.parentId === origin.id) {
      return;
    }

    const isSidebarMovementOnly =
      (destination.source === "sidebar" && origin.source === "sidebar") ||
      (origin.source === "sidebar" && destination.source === "mainTable");

    // Determine shipment IDs based on drag context
    let shipmentIds: number[] =
      sidebarSelection.length > 0
        ? getShipmentIdsFromSelection(sidebarSelection, origin.source)
        : [Number(origin.id)];

    if (origin.source !== "unassigned") {
      // Handle table selection for non-sidebar movements
      if (!isSidebarMovementOnly && tableSelection.length > 0) {
        shipmentIds = [...tableSelection.map(Number)];
        shipmentIds.push(Number(origin.id));
      }
    }

    // Remove duplicates
    shipmentIds = shipmentIds.filter(
      (id, index, array) => array.indexOf(id) === index
    );

    const key = `${origin.source}->${destination.source}`;
    const action = actionMap[key];

    invariant(action, `No action defined for movement: ${key}`);

    switch (action) {
      case "assign":
        handleAssignShipments({
          location,
          destinationIndex,
          origin,
          destination,
          shipmentIds,
        });
        clearSelection();
        break;
      case "unassign":
        handleUnassignShipments({
          sourceId: origin.parentId || origin.id || "",
          sourceType: origin.type,
          shipmentIds,
        });
        clearSelection();
        break;
      default:
        throw new Error(`Unhandled action type: ${action}`);
    }
  };

  return { handleDrop };
};

interface GetStopSequenceArgs {
  plan: PlanTableFormat;
  destinationIndex: number;
  location: DragLocationHistory;
}

export const getStopSequence = ({
  plan,
  location,
  destinationIndex,
}: GetStopSequenceArgs) => {
  const locationData = location.current.dropTargets[0].data;
  const symbols = Object.getOwnPropertySymbols(locationData);
  const closestEdge = locationData[symbols[0]];

  if (!closestEdge) return undefined;

  const edge = z.enum(["top", "bottom"]).parse(closestEdge);

  return getAfterShipmentId(plan.shipments, destinationIndex, edge);
};

interface InboundDragAndDropHandlersProps {
  plannedRoutes: PlanSummary[];
  shipments: Shipment[];
  planDate: number;
  sicId: number;
  shipmentActionDialogRef: RefObject<ShipmentActionDialogRef>;
  confirmDialogRef: RefObject<ConfirmShipmentMoveDialogRef>;
  reOrderShipment: (payload: ReorderPayload) => void;
  assignShipment: (request: AssignToRequest) => void;
  unassignShipment: (request: UnassignRequest) => void;
}

export const useInboundDragAndDropHandlers = ({
  plannedRoutes,
  shipments,
  planDate,
  sicId,
  shipmentActionDialogRef,
  confirmDialogRef,
  reOrderShipment,
  assignShipment,
  unassignShipment,
}: InboundDragAndDropHandlersProps) => {
  const { handleDrop } = useInboundDragAndDrop();

  const isClosedForSequencingFlag = useIsFeatureFlagEnabled(
    "inbound-3771-update-behavior-of-routes-CLDV-DISP-DEPT-client"
  );

  const isCNCLTrapFeatureEnabled = useIsFeatureFlagEnabled(
    "operations-5594-CNCL-traps-treatment-client"
  );

  const isImmutableRoute = useCallback(
    (plan: PlanTableFormat | undefined) =>
      isRouteImmutablePure(plan?.status, plan?.shipments),
    []
  );

  const plans: PlanTableFormat[] = useMemo(
    () =>
      getFormattedPlannedRoutes({
        sicId,
        date: planDate,
        plannedRoutes,
        includeUnassigned: true,
        shipments,
      }),
    [planDate, plannedRoutes, shipments, sicId]
  );

  const handleAssignShipments: AssignShipmentFn = useCallback(
    ({ destination, origin, shipmentIds, destinationIndex, location }) => {
      const destinationId = destination.parentId || destination.id || "";

      // For unassigned shipments, there's no container ID (they're not in a route/trap)
      const originId =
        origin.source === "unassigned"
          ? "undefined"
          : origin.parentId || origin.id || "";

      const destinationPlan = plans.find(
        (plan) => parseInt(destinationId) === plan.id
      );

      if (
        destination.type === "trap" &&
        checkIsTrapCancelled(destinationPlan?.status, isCNCLTrapFeatureEnabled)
      ) {
        toast(toastMessage.inbound.assign.unableToAssignToCancelledTrap, {
          variant: "warning",
        });
        return;
      }

      // Check if destination route is immutable (DEPT route with DLVR shipments)
      if (isImmutableRoute(destinationPlan)) {
        toast("You cannot assign shipments to a DEPT route with DLVR bills.", {
          variant: "warning",
        });
        return;
      }

      const originPlan = plans.find((plan) => parseInt(originId) === plan.id);

      if (destinationPlan === undefined) {
        shipmentActionDialogRef.current?.close();
        throw new Error(
          `Droppable Destination not found with type: ${destination.type} and id: ${destinationId}`
        );
      }

      // compute drop metrics for assignment, only allow header drop for sidebar<->mainTable
      // Only allow header drop when dragging from outside the table into the mainTable
      const allowHeaderDrop =
        destination.source === "mainTable" && origin.source !== "mainTable";

      const { isDroppedOnHeader, afterShipmentId, afterRouteStop } =
        getDropMetrics(
          destinationPlan,
          location,
          destinationIndex,
          allowHeaderDrop
        );

      // Reorder shipments (only if same container type AND id)
      if (destination.parentId === origin.parentId) {
        // Prevent reordering on routes closed for sequencing (DISP, CLDV, DEPT)
        const isSidebarRouteClosedForSequencing =
          isClosedForSequencingFlag &&
          isRouteClosedForSequencing([destinationPlan]);

        if (isSidebarRouteClosedForSequencing) {
          toast(toastMessage.inbound.reorder.unableReorderMessage, {
            variant: "warning",
          });
          return;
        }

        // Prevent reordering on immutable routes
        if (isImmutableRoute(destinationPlan)) {
          toast(
            "You cannot reorder shipments on a DEPT route with DLVR bills.",
            { variant: "warning" }
          );
          return;
        }
        if (origin.type === "trap") {
          toast(toastMessage.inbound.autoSequencing.notAllowedOnTraps, {
            variant: "error",
          });
          return;
        }

        if (destinationPlan.shipments.length > 0) {
          const afterShipmentId =
            getStopSequence({
              plan: destinationPlan,
              destinationIndex,
              location,
            }) || 0;
          shipmentActionDialogRef.current?.open("loading", "reorder");
          reOrderShipment({
            routeId: Number(destinationId),
            data: {
              shipmentIds,
              afterShipmentId,
            },
          });
        }
        return;
      }

      const shipmentToMove: ShipmentToMove = {
        destination: {
          name: destinationPlan.name,
          status: destinationPlan.status,
          type: destination.type,
        },
        source: {
          name: originPlan?.name || "",
          status: originPlan?.status || "",
          type: origin.type,
        },
        shipmentIds,
        rollbackMessage: undefined,
        onConfirm: () => {
          assignShipment({
            origins: [
              {
                id: originId,
                type: origin.source === "unassigned" ? "shipment" : origin.type,
                shipmentIds,
              },
            ],
            target: {
              date: planDate,
              sicId,
              id: destinationId,
              type: destination.type,
              afterShipmentId,
              ...getAfterRouteStopParam(afterRouteStop),
              isDroppedOnHeader,
            },
          });
        },
      };

      // On handleAssign, Destination is ALWAYS Route/Trap
      const onlyDestinationIsRouteOrTrap =
        originPlan?.type !== "delivery_route" &&
        originPlan?.type !== "delivery_trap";

      const bothAreRouteOrTrap =
        originPlan?.type === "delivery_route" ||
        originPlan?.type === "delivery_trap";

      // Shows rollback message saying that both going to return to CLDK
      if (
        bothAreRouteOrTrap &&
        !destinationPlan.isAvailableForMassage &&
        !originPlan.isAvailableForMassage
      ) {
        confirmDialogRef.current?.open({
          ...shipmentToMove,
          rollbackMessage: {
            toDestination: true,
            toSource: true,
          },
        });
      }
      // Shows rollback message only for the not available one
      else if (
        bothAreRouteOrTrap &&
        (!destinationPlan.isAvailableForMassage ||
          !originPlan.isAvailableForMassage)
      ) {
        confirmDialogRef.current?.open({
          ...shipmentToMove,
          rollbackMessage: {
            toDestination: !destinationPlan.isAvailableForMassage,
            toSource: !originPlan.isAvailableForMassage,
          },
        });
      }
      // Shows rollback message only to the destination
      else if (
        onlyDestinationIsRouteOrTrap &&
        !destinationPlan.isAvailableForMassage
      ) {
        confirmDialogRef.current?.open({
          ...shipmentToMove,
          rollbackMessage: {
            toDestination: true,
            toSource: false,
          },
        });
      }
      // DON'T SHOW MODAL
      else {
        shipmentActionDialogRef.current?.open("loading", "assign");
        assignShipment({
          origins: [
            {
              id: originId,
              type: origin.source === "unassigned" ? "shipment" : origin.type,
              shipmentIds,
            },
          ],
          target: {
            date: planDate,
            sicId,
            id: destinationId,
            type: destination.type,
            afterShipmentId,
            ...getAfterRouteStopParam(afterRouteStop),
            isDroppedOnHeader,
          },
        });
      }
    },
    [
      assignShipment,
      confirmDialogRef,
      planDate,
      plans,
      reOrderShipment,
      shipmentActionDialogRef,
      sicId,
      isImmutableRoute,
      isClosedForSequencingFlag,
      isCNCLTrapFeatureEnabled,
    ]
  );

  const handleUnassignShipments: UnassignShipmentFn = useCallback(
    ({ shipmentIds, sourceId = "", sourceType }) => {
      const source = plans.find((trap) => parseInt(sourceId) === trap.id);

      if (!source) {
        shipmentActionDialogRef.current?.close();
        throw new Error(
          `Draggable source not found with type: ${sourceType} and id: ${sourceId}`
        );
      }

      const unassignShipmentFn = () =>
        unassignShipment({
          origins: [
            {
              id: sourceId,
              type: sourceType,
              shipmentIds,
            },
          ],
          target: {
            date: planDate,
            sicId,
            type: "unassign",
          },
        });

      // Shows rollback message only to the source
      if (!source.isAvailableForMassage) {
        confirmDialogRef.current?.open({
          destination: {
            name: "unassign",
            type: "shipment",
          },
          source: {
            name: source.name,
            status: source.status,
            type: sourceType,
          },
          shipmentIds,
          rollbackMessage: {
            toDestination: false,
            toSource: true,
          },
          onConfirm: unassignShipmentFn,
        });
      } else {
        shipmentActionDialogRef.current?.open("loading", "unassign");
        unassignShipmentFn();
      }
    },
    [
      confirmDialogRef,
      planDate,
      plans,
      shipmentActionDialogRef,
      sicId,
      unassignShipment,
    ]
  );

  const dropHandler = useCallback(
    ({ source, location }: BaseEventPayload<ElementDragType>) => {
      handleDrop({
        source,
        location,
        handleAssignShipments,
        handleUnassignShipments,
      });
    },
    [handleAssignShipments, handleDrop, handleUnassignShipments]
  );

  return { dropHandler };
};
